import { signal } from "@angular/core";
import { ACC_TIME_SLOT, DAY_INDICES, HOURS_ET, MeetingSlots, SLOTS_DAY_MAP, StandardFourTimeZoneId, WeekdayIndex } from "../../../types/common";
import { getCommonLabels } from '../../url/url.service.labels';
import { DateUtilities } from "./index";


export function formatMeetingSlots(meetingSlots: MeetingSlots | undefined, timeZoneId: StandardFourTimeZoneId, isEnglish: boolean): string[] {
	if (!meetingSlots) return [];

	return DAY_INDICES
		.map(day => formatDaySlots(day as WeekdayIndex, meetingSlots[SLOTS_DAY_MAP[day]], timeZoneId, isEnglish))
		.filter(daySlot => daySlot !== null) as string[];
}

/**
 * Formats the meeting slots for a given day and timezone.
 * @param dayIndex The index of the day of the week (0-6).
 * @param slot The string representing the meeting slots for the day.
 * @param timeZoneId The timezone ID for which to format the slots.
 * @param isEnglish Whether to use English labels.
 * @returns A string representing the time range for the day of the week, or null if there are no meeting slots.
 */
function formatDaySlots(dayIndex: WeekdayIndex, slot: string, timeZoneId: StandardFourTimeZoneId, isEnglish: boolean): string | null {
	const mergedRanges = extractAndMergeTimeRanges(dayIndex, slot, timeZoneId, isEnglish);
	if (mergedRanges.length === 0) return null;

	return formatMergedRanges(dayIndex, mergedRanges);
}

/**
 * Extracts time ranges and merges consecutive ones in a single pass.
 * @param dayIndex The index of the day of the week (0-6).
 * @param slot The string representing the meeting slots for the day.
 * @param timeZoneId The timezone ID for which to format the slots.
 * @param isEnglish Whether to use English labels.
 * @returns An array of merged time range strings for the day of the week.
 */
function extractAndMergeTimeRanges(dayIndex: WeekdayIndex, slot: string, timeZoneId: StandardFourTimeZoneId, isEnglish: boolean): string[] {
	const timeRanges: string[] = [];
	const day = getCommonLabels(signal(isEnglish)).DayNamesMap.long[dayIndex]();
	let startIndex = -1;
	let currentRange = '';

	for (let i = 0; i < slot.length; i++) {
		const isSelected = slot[i] !== '-';
		const isLast = i === slot.length - 1;

		if (isSelected && startIndex === -1) {
			startIndex = i;
		}

		if (startIndex !== -1 && (!isSelected || isLast)) {
			const endIndex = isLast && isSelected ? i : i - 1;
			const timeRange = DateUtilities.convertETtoTimeZone(
				HOURS_ET[startIndex]!,
				timeZoneId,
				endIndex - startIndex + ACC_TIME_SLOT
			);

			// Check if this range is consecutive to the previous one
			if (currentRange && areConsecutive(currentRange, timeRange)) {
				// Merge with the current range instead of adding to array
				currentRange = mergeTwo(currentRange, timeRange);
			} else {
				// Push the previous range if it exists
				if (currentRange) {
					timeRanges.push(currentRange);
				}
				currentRange = timeRange;
			}

			startIndex = -1;
		}
	}

	// Don't forget to push the last range
	if (currentRange) {
		timeRanges.push(currentRange);
	}

	// Format the final result with day name only once
	if (timeRanges.length === 0) return [];

	return [`${day}: ${timeRanges.join(', ')}`];
}

/**
 * Checks if two time ranges are consecutive.
 * @param current The current time range.
 * @param next The next time range.
 * @returns True if the current time range ends at the same time as the next time range starts, false otherwise.
 */
function areConsecutive(current: string, next: string): boolean {
	const currentEndTime = extractEndTimeFromRange(current);
	const nextStartTime = extractStartTimeFromRange(next);
	return currentEndTime === nextStartTime;
}

/**
 * Merges two time ranges into a single range.
 * The second time range is assumed to start at the same time as the first time range ends.
 * @param current The current time range.
 * @param next The next time range.
 * @returns A string representing the merged time range.
 */
function mergeTwo(current: string, next: string): string {
	const nextEndTime = extractEndTimeFromRange(next);
	return current.replace(/-\s*(\d+(?::\d+)?(?:am|pm))/i, ` - ${nextEndTime}`);
}

/**
 * Extracts the end time from a time range string (without day prefix).
 * @param timeRange The time range string in the format "HH:MM AM/PM - HH:MM AM/PM"
 * @returns The end time in the format "hh:mm am/pm" or null if the time range is invalid.
 */
function extractEndTimeFromRange(timeRange: string): string | null {
	const match = timeRange.match(/-\s*(\d+(?::\d+)?(?:am|pm))/i);
	return match ? match[1]!.toLowerCase() : null;
}

/**
 * Extracts the start time from a time range string (without day prefix).
 * @param timeRange The time range string in the format "HH:MM AM/PM - HH:MM AM/PM"
 * @returns The start time in the format "hh:mm am/pm" or null if the time range is invalid.
 */
function extractStartTimeFromRange(timeRange: string): string | null {
	const match = timeRange.match(/(\d+(?::\d+)?(?:am|pm))\s*-/i);
	return match ? match[1]!.toLowerCase() : null;
}

/**
 * Formats the merged time ranges into a single string.
 * @param dayIndex The index of the day of the week (0-6).
 * @param mergedRanges The merged time ranges for the day.
 * @returns A string representing the merged time ranges, separated by commas.
 */
function formatMergedRanges(dayIndex: WeekdayIndex, mergedRanges: string[]): string {
	return mergedRanges.join(', ');
}